"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "Initial Assessment",
  description: "We begin with a comprehensive review of your medical history and a detailed discussion of your current concerns to fully understand your unique needs."
}, {
  title: "In-Depth Discussion",
  description: "Our specialists will explain their findings in clear, accessible language, ensuring you have a complete understanding of your condition and the available options."
}, {
  title: "Personalised Planning",
  description: "Together, we will develop a tailored treatment strategy that aligns with your lifestyle, preferences, and specific health goals."
}, {
  title: "Treatment & Care",
  description: "Your procedure or therapy is conducted with the utmost focus on your comfort, safety, and well-being, utilising evidence-based practices."
}, {
  title: "Follow-Up & Support",
  description: "We provide clear post-appointment instructions and schedule necessary follow-up visits to monitor your progress and ensure optimal outcomes."
}];
function StepItem({
  step,
  index,
  isLast
}: {
  step: (typeof steps)[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    rootMargin: "0px 0px -50px 0px"
  });
  return <div ref={ref} data-index={index} className={`relative pb-16 md:pb-24 last:pb-0 group transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {!isLast && <div className="absolute left-[1.5rem] md:left-[2.5rem] top-24 bottom-[-2rem] w-px border-l-[2px] border-dotted border-muted/60 hidden sm:block" />}

      <div className="relative pl-4 sm:pl-20 md:pl-32 z-10">
        <span className="absolute left-0 sm:left-4 md:left-8 -top-8 sm:-top-10 md:-top-14 text-7xl sm:text-8xl md:text-[10rem] font-display font-bold text-muted/30 -z-10 select-none leading-none tracking-tighter transition-colors duration-500 group-hover:text-muted/50">
          0{index + 1}
        </span>

        <div className="pt-4 sm:pt-2 md:pt-6">
          <h3 className="text-2xl md:text-3xl font-display font-semibold text-foreground mb-3 md:mb-4 tracking-tight">
            {step.title}
          </h3>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl leading-relaxed">
            {step.description}
          </p>
        </div>
      </div>
    </div>;
}
export default function WhatHappensAtYourAppointment() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="what-happens-at-your-appointment" className="bg-background py-20 md:py-32 overflow-hidden isolate">
      <div className="container mx-auto px-6 md:px-12 max-w-5xl">
        <div ref={headerRef} className={`mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-6 tracking-tight">
            What Happens at Your Appointment
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            We believe in complete transparency. Here is a step-by-step guide to what you can expect when you visit our clinic, designed to ensure your comfort and clarity at every stage.
          </p>
        </div>

        <div className="relative">
          {steps.map((step, index) => <StepItem key={index} step={step} index={index} isLast={index === steps.length - 1} />)}
        </div>

        <div className="mt-16 md:mt-24 pt-8 border-t border-muted/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h4 className="text-xl font-display font-semibold text-foreground mb-2">
              Ready to take the next step?
            </h4>
            <p className="text-muted-foreground">
              Schedule your initial assessment today.
            </p>
          </div>
          <Link to="https://align-physiotherapy.uk3.cliniko.com/bookings?business_id=1869864670421067946&practitioner_id=1869864668449745369&appointment_type_id=1869864670219741836">
            <Button size="lg" className="group">
              Book Appointment
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}